/**
 * Admin Context
 *
 * Global state management for admin panel.
 * Loads all clients, users, and reports upfront.
 * Provides CRUD operations with optimistic updates.
 */
import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
  type ReactNode,
} from 'react';
import { api } from '../services/api';
import { useToast } from './ToastContext';
import type { Client, User, Report, ClientFormData, UserFormData, ReportFormData } from '../types';

interface AdminContextType {
  // Data
  clients: Client[];
  users: User[];
  reports: Report[];

  // Loading state
  isLoading: boolean;
  error: string | null;

  // Filters
  selectedClientId: string | null;
  setSelectedClientId: (id: string | null) => void;

  // Filtered data helpers
  filteredUsers: User[];
  filteredReports: Report[];

  // Client CRUD
  createClient: (data: ClientFormData) => Promise<Client | null>;
  updateClient: (id: string, data: Partial<Client>) => Promise<Client | null>;
  deleteClient: (id: string) => Promise<boolean>;

  // User CRUD
  createUser: (data: UserFormData) => Promise<User | null>;
  updateUser: (id: string, data: Partial<User>) => Promise<User | null>;
  deleteUser: (id: string) => Promise<boolean>;

  // Report CRUD
  createReport: (data: ReportFormData) => Promise<Report | null>;
  updateReport: (id: string, data: Partial<Report>) => Promise<Report | null>;
  deleteReport: (id: string) => Promise<boolean>;
  reorderReports: (clientId: string, orderedIds: string[]) => Promise<boolean>;

  // Permissions management
  getViewerPermissions: (viewerId: string) => Promise<string[]>;
  updateViewerPermissions: (viewerId: string, reportIds: string[]) => Promise<boolean>;

  // Refresh data
  refresh: () => Promise<void>;

  // Utility
  getClientById: (id: string) => Client | undefined;
  getReportCountByClient: (clientId: string) => number;
  getUserCountByClient: (clientId: string) => number;
  getViewersByClient: (clientId: string) => User[];
}

const AdminContext = createContext<AdminContextType | null>(null);

export const useAdmin = (): AdminContextType => {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error('useAdmin must be used within AdminProvider');
  }
  return context;
};

interface AdminProviderProps {
  children: ReactNode;
}

export const AdminProvider = ({ children }: AdminProviderProps) => {
  const { showSuccess, showError } = useToast();

  // State
  const [clients, setClients] = useState<Client[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [reports, setReports] = useState<Report[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);

  // Filtered data
  const filteredUsers = useMemo(() => {
    if (!selectedClientId) return users;
    return users.filter((u) => u.clientId === selectedClientId);
  }, [users, selectedClientId]);

  const filteredReports = useMemo(() => {
    if (!selectedClientId) return reports;
    return reports.filter((r) => r.clientId === selectedClientId);
  }, [reports, selectedClientId]);

  // Load all data
  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const [clientsRes, usersRes, reportsRes] = await Promise.all([
        api.admin.getClients(),
        api.admin.getUsers(),
        api.admin.getReports(),
      ]);

      if (clientsRes.error) throw new Error(clientsRes.error);
      if (usersRes.error) throw new Error(usersRes.error);
      if (reportsRes.error) throw new Error(reportsRes.error);

      setClients(clientsRes.data || []);
      setUsers(usersRes.data || []);
      setReports(reportsRes.data || []);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load admin data';
      setError(message);
      showError(message);
    } finally {
      setIsLoading(false);
    }
  }, [showError]);

  // Load data on mount
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Utility functions
  const getClientById = useCallback(
    (id: string) => clients.find((c) => c.id === id),
    [clients]
  );

  const getReportCountByClient = useCallback(
    (clientId: string) => reports.filter((r) => r.clientId === clientId).length,
    [reports]
  );

  const getUserCountByClient = useCallback(
    (clientId: string) => users.filter((u) => u.clientId === clientId).length,
    [users]
  );

  // Client CRUD
  const createClient = useCallback(
    async (data: ClientFormData): Promise<Client | null> => {
      const res = await api.admin.createClient(data);
      if (res.error) {
        showError(res.error);
        return null;
      }
      if (res.data) {
        setClients((prev) => [...prev, res.data!]);
        showSuccess('Client created successfully');
        return res.data;
      }
      return null;
    },
    [showSuccess, showError]
  );

  const updateClient = useCallback(
    async (id: string, data: Partial<Client>): Promise<Client | null> => {
      // Optimistic update
      const originalClients = clients;
      setClients((prev) =>
        prev.map((c) => (c.id === id ? { ...c, ...data } : c))
      );

      const res = await api.admin.updateClient(id, data);
      if (res.error) {
        // Rollback
        setClients(originalClients);
        showError(res.error);
        return null;
      }
      if (res.data) {
        // Update with server response
        setClients((prev) =>
          prev.map((c) => (c.id === id ? res.data! : c))
        );
        showSuccess('Client updated successfully');
        return res.data;
      }
      return null;
    },
    [clients, showSuccess, showError]
  );

  const deleteClient = useCallback(
    async (id: string): Promise<boolean> => {
      const res = await api.admin.deleteClient(id);
      if (res.error) {
        showError(res.error);
        return false;
      }
      setClients((prev) => prev.filter((c) => c.id !== id));
      showSuccess('Client deleted successfully');
      return true;
    },
    [showSuccess, showError]
  );

  // User CRUD
  const createUser = useCallback(
    async (data: UserFormData): Promise<User | null> => {
      const res = await api.admin.createUser(data);
      if (res.error) {
        showError(res.error);
        return null;
      }
      if (res.data) {
        setUsers((prev) => [...prev, res.data!]);
        showSuccess('User created successfully');
        return res.data;
      }
      return null;
    },
    [showSuccess, showError]
  );

  const updateUser = useCallback(
    async (id: string, data: Partial<User>): Promise<User | null> => {
      // Optimistic update
      const originalUsers = users;
      setUsers((prev) =>
        prev.map((u) => (u.id === id ? { ...u, ...data } : u))
      );

      const res = await api.admin.updateUser(id, data);
      if (res.error) {
        // Rollback
        setUsers(originalUsers);
        showError(res.error);
        return null;
      }
      if (res.data) {
        setUsers((prev) =>
          prev.map((u) => (u.id === id ? res.data! : u))
        );
        showSuccess('User updated successfully');
        return res.data;
      }
      return null;
    },
    [users, showSuccess, showError]
  );

  const deleteUser = useCallback(
    async (id: string): Promise<boolean> => {
      const res = await api.admin.deleteUser(id);
      if (res.error) {
        showError(res.error);
        return false;
      }
      setUsers((prev) => prev.filter((u) => u.id !== id));
      showSuccess('User deleted successfully');
      return true;
    },
    [showSuccess, showError]
  );

  // Report CRUD
  const createReport = useCallback(
    async (data: ReportFormData): Promise<Report | null> => {
      const res = await api.admin.createReport(data);
      if (res.error) {
        showError(res.error);
        return null;
      }
      if (res.data) {
        setReports((prev) => [...prev, res.data!]);
        showSuccess('Report created successfully');
        return res.data;
      }
      return null;
    },
    [showSuccess, showError]
  );

  const updateReport = useCallback(
    async (id: string, data: Partial<Report>): Promise<Report | null> => {
      // Optimistic update
      const originalReports = reports;
      setReports((prev) =>
        prev.map((r) => (r.id === id ? { ...r, ...data } : r))
      );

      const res = await api.admin.updateReport(id, data);
      if (res.error) {
        // Rollback
        setReports(originalReports);
        showError(res.error);
        return null;
      }
      if (res.data) {
        setReports((prev) =>
          prev.map((r) => (r.id === id ? res.data! : r))
        );
        showSuccess('Report updated successfully');
        return res.data;
      }
      return null;
    },
    [reports, showSuccess, showError]
  );

  const deleteReport = useCallback(
    async (id: string): Promise<boolean> => {
      const res = await api.admin.deleteReport(id);
      if (res.error) {
        showError(res.error);
        return false;
      }
      setReports((prev) => prev.filter((r) => r.id !== id));
      showSuccess('Report deleted successfully');
      return true;
    },
    [showSuccess, showError]
  );

  // Permissions management
  const getViewerPermissions = useCallback(
    async (viewerId: string): Promise<string[]> => {
      const res = await api.admin.getViewerPermissions(viewerId);
      if (res.error) {
        showError(res.error);
        return [];
      }
      return res.data?.permissions || [];
    },
    [showError]
  );

  const updateViewerPermissions = useCallback(
    async (viewerId: string, reportIds: string[]): Promise<boolean> => {
      const res = await api.admin.updateViewerPermissions(viewerId, reportIds);
      if (res.error) {
        showError(res.error);
        return false;
      }
      showSuccess('Permissions updated successfully');
      return true;
    },
    [showSuccess, showError]
  );

  // Get viewers for a specific client
  const getViewersByClient = useCallback(
    (clientId: string) => users.filter((u) => u.clientId === clientId && u.role === 'viewer'),
    [users]
  );

  const reorderReports = useCallback(
    async (clientId: string, orderedIds: string[]): Promise<boolean> => {
      // Get reports for this client and update their order
      const clientReports = reports.filter((r) => r.clientId === clientId);
      const originalReports = [...reports];

      // Optimistic update - update order based on position in orderedIds
      const updatedReports = reports.map((r) => {
        if (r.clientId !== clientId) return r;
        const newOrder = orderedIds.indexOf(r.id);
        return newOrder >= 0 ? { ...r, order: newOrder } : r;
      });
      setReports(updatedReports);

      // Update each report's order on the server
      try {
        const updatePromises = clientReports.map((report) => {
          const newOrder = orderedIds.indexOf(report.id);
          if (newOrder >= 0 && newOrder !== report.order) {
            return api.admin.updateReport(report.id, { order: newOrder });
          }
          return Promise.resolve({ data: report } as { data: Report });
        });

        const results = await Promise.all(updatePromises);
        const hasError = results.some((r) => 'error' in r && r.error);

        if (hasError) {
          setReports(originalReports);
          showError('Failed to reorder reports');
          return false;
        }

        showSuccess('Reports reordered successfully');
        return true;
      } catch {
        setReports(originalReports);
        showError('Failed to reorder reports');
        return false;
      }
    },
    [reports, showSuccess, showError]
  );

  const value: AdminContextType = {
    clients,
    users,
    reports,
    isLoading,
    error,
    selectedClientId,
    setSelectedClientId,
    filteredUsers,
    filteredReports,
    createClient,
    updateClient,
    deleteClient,
    createUser,
    updateUser,
    deleteUser,
    createReport,
    updateReport,
    deleteReport,
    reorderReports,
    getViewerPermissions,
    updateViewerPermissions,
    refresh: loadData,
    getClientById,
    getReportCountByClient,
    getUserCountByClient,
    getViewersByClient,
  };

  return (
    <AdminContext.Provider value={value}>
      {children}
    </AdminContext.Provider>
  );
};

export default AdminContext;
